import { useEffect, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import { Badge, Button, Card } from "@contractor-quoting/shared-ui";
import { Link } from "react-router";
import { SaveToast, useSaveToast } from "../components/SaveToast.js";
export { ConfigurationImportPage } from "../components/ConfigurationImportPage.js";

const commonTimezones = [
  ["America/St_Johns", "Newfoundland — St. John's"],
  ["America/Halifax", "Atlantic — Halifax"],
  ["America/Toronto", "Eastern — Toronto"],
  ["America/New_York", "Eastern — New York"],
  ["America/Winnipeg", "Central — Winnipeg"],
  ["America/Chicago", "Central — Chicago"],
  ["America/Mexico_City", "Central — Mexico City"],
  ["America/Edmonton", "Mountain — Edmonton"],
  ["America/Denver", "Mountain — Denver"],
  ["America/Phoenix", "Arizona — Phoenix (no daylight saving)"],
  ["America/Vancouver", "Pacific — Vancouver"],
  ["America/Los_Angeles", "Pacific — Los Angeles"],
  ["America/Anchorage", "Alaska — Anchorage"],
  ["Pacific/Honolulu", "Hawaii — Honolulu"],
] as const;

export function ContractorTeamPage() {
  interface TeamData {
    members: Array<{ id: string; display_name: string | null; email: string; role: string }>;
    invitations: Array<{
      id: string;
      invited_email: string;
      expires_at: string;
      status: string;
      delivery_status: string;
    }>;
  }
  const [data, setData] = useState<TeamData>({
    members: [],
    invitations: [],
  });
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const load = () => apiFetch<typeof data>("/v1/contractor/team").then(setData);
  useEffect(() => {
    void load();
  }, []);
  const invite = async () => {
    await apiFetch("/v1/contractor/team/invitations", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    setEmail("");
    setMessage("Invitation created. Delivery is pending.");
    await load();
  };
  return (
    <div>
      <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
        Workspace access
      </p>
      <h1 className="mt-2 text-4xl font-black">Team</h1>
      <Card className="mt-7">
        <h2 className="text-xl font-black">Invite a staff member</h2>
        <div className="mt-4 flex gap-2">
          <input
            type="email"
            className="min-w-0 flex-1 rounded-xl border border-stone-300 p-3"
            placeholder="staff@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button disabled={!email} onClick={() => void invite()}>
            Send invitation
          </Button>
        </div>
        {message ? <p className="mt-2 text-sm font-bold text-emerald-700">{message}</p> : null}
      </Card>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-xl font-black">Active members</h2>
          <div className="mt-3 divide-y">
            {data.members.map((member) => (
              <div className="flex items-center justify-between py-3" key={member.id}>
                <div>
                  <p className="font-bold">{member.display_name || member.email}</p>
                  <p className="text-sm text-stone-500">{member.email}</p>
                </div>
                <Badge>{member.role}</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h2 className="text-xl font-black">Pending invitations</h2>
          <div className="mt-3 divide-y">
            {data.invitations.map((invite) => (
              <div className="flex items-center justify-between py-3" key={invite.id}>
                <div>
                  <p className="font-bold">{invite.invited_email}</p>
                  <p className="text-sm text-stone-500">
                    Expires {new Date(invite.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge>{invite.status}</Badge>
                  <span className="text-xs font-semibold text-stone-500">
                    Email:{" "}
                    {invite.delivery_status === "queued"
                      ? "provider accepted"
                      : invite.delivery_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function ContractorSettingsPage() {
  interface SettingsRecord {
    id: string;
    name: string;
    quoting_enabled: boolean;
    public_description: string;
    phone: string | null;
    email: string | null;
    brand_primary: string;
    service_area: string | null;
    timezone: string;
    approver_name: string | null;
    business_address_line1: string | null;
    business_address_line2: string | null;
    business_city: string | null;
    business_region: string | null;
    business_postal_code: string | null;
    business_country: string;
    website: string | null;
    tax_registration_number: string | null;
    licence_number: string | null;
    intake_ready: boolean;
    readiness_issues: string[];
  }
  interface ServiceCity {
    id: string;
    city: string;
    region: string;
    country: string;
    active: boolean;
  }
  const [settings, setSettings] = useState<SettingsRecord | null>(null);
  const [cities, setCities] = useState<ServiceCity[]>([]);
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("ON");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useSaveToast();
  const load = async () => {
    const [settingsResult, cityResult] = await Promise.all([
      apiFetch<{ items: SettingsRecord[] }>("/v1/contractor/settings"),
      apiFetch<{ items: ServiceCity[] }>("/v1/contractor/service-cities"),
    ]);
    setSettings(settingsResult.items[0] ?? null);
    setCities(cityResult.items);
  };
  useEffect(() => {
    void load();
  }, []);
  if (!settings) return <p>Loading settings…</p>;
  const save = async () => {
    setError("");
    toast.clear();
    setSaving(true);
    try {
      await apiFetch(`/v1/contractor/settings/${settings.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: settings.name,
          quotingEnabled: settings.quoting_enabled,
          publicDescription: settings.public_description,
          email: settings.email || null,
          brandPrimary: settings.brand_primary,
          phone: settings.phone || null,
          serviceArea: settings.service_area,
          timezone: settings.timezone,
          approverName: settings.approver_name || null,
          businessAddressLine1: settings.business_address_line1 || null,
          businessAddressLine2: settings.business_address_line2 || null,
          businessCity: settings.business_city || null,
          businessRegion: settings.business_region || null,
          businessPostalCode: settings.business_postal_code || null,
          businessCountry: settings.business_country || "CA",
          website: settings.website || null,
          taxRegistrationNumber: settings.tax_registration_number || null,
          licenceNumber: settings.licence_number || null,
        }),
      });
      toast.show("Settings saved");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Settings could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const addCity = async () => {
    await apiFetch("/v1/contractor/service-cities", {
      method: "POST",
      body: JSON.stringify({ city, region, country: "CA" }),
    });
    setCity("");
    await load();
  };
  return (
    <div>
      <SaveToast message={toast.message} />
      <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
        Contractor account
      </p>
      <h1 className="mt-2 text-4xl font-black">Settings & profile</h1>
      <div className="mt-7 grid gap-6 xl:grid-cols-2">
        <Card>
          <h2 className="text-xl font-black">Business profile</h2>
          <div className="mt-4 grid gap-4">
            {(
              [
                ["name", "Business name"],
                ["phone", "Phone"],
                ["email", "Notification email"],
                ["brand_primary", "Brand colour"],
                ["approver_name", "Owner / approver name"],
                ["business_address_line1", "Business address"],
                ["business_address_line2", "Business address line 2"],
                ["business_city", "Business city"],
                ["business_region", "Province / state"],
                ["business_postal_code", "Postal / ZIP code"],
                ["business_country", "Business country"],
                ["website", "Website"],
                ["tax_registration_number", "GST/HST number"],
                ["licence_number", "Licence number (optional)"],
              ] as const
            ).map(([key, label]) => (
              <label key={key}>
                <span className="text-sm font-bold">{label}</span>
                <input
                  className="mt-1 w-full rounded-xl border p-3"
                  value={settings[key] ?? ""}
                  onChange={(event) =>
                    setSettings((current) =>
                      current ? { ...current, [key]: event.target.value } : current,
                    )
                  }
                />
              </label>
            ))}
            <label>
              <span className="text-sm font-bold">Business timezone</span>
              <select
                className="mt-1 w-full rounded-xl border p-3"
                value={settings.timezone}
                onChange={(event) => {
                  setError("");
                  setSettings({ ...settings, timezone: event.target.value });
                }}
              >
                {!commonTimezones.some(([value]) => value === settings.timezone) ? (
                  <option value={settings.timezone}>{settings.timezone} — current</option>
                ) : null}
                {commonTimezones.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="text-sm font-bold">Public description</span>
              <textarea
                className="mt-1 min-h-28 w-full rounded-xl border p-3"
                value={settings.public_description}
                onChange={(event) =>
                  setSettings({ ...settings, public_description: event.target.value })
                }
              />
            </label>
            <label className="flex items-center gap-2 font-bold">
              <input
                type="checkbox"
                checked={settings.quoting_enabled}
                onChange={(event) => {
                  if (event.target.checked && !settings.intake_ready) {
                    setError(
                      settings.readiness_issues[0] ?? "Complete project configuration first.",
                    );
                    return;
                  }
                  setError("");
                  setSettings({ ...settings, quoting_enabled: event.target.checked });
                }}
              />{" "}
              Accept new customer projects
            </label>
            {!settings.intake_ready ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                <p className="font-bold">Customer intake is not ready</p>
                {settings.readiness_issues.map((issue) => (
                  <p className="mt-1" key={issue}>
                    {issue}
                  </p>
                ))}
                <Link className="mt-2 inline-block font-bold underline" to="/contractor/runbooks">
                  Configure job runbooks
                </Link>
              </div>
            ) : null}
            <Button disabled={saving} onClick={() => void save()}>
              {saving ? "Saving…" : "Save settings"}
            </Button>
            {error ? <p className="text-sm font-bold text-red-700">{error}</p> : null}
          </div>
        </Card>
        <Card>
          <h2 className="text-xl font-black">Service cities</h2>
          <p className="mt-2 text-sm text-stone-500">
            Exact city matching powers the customer marketplace. Full addresses stay private.
          </p>
          <div className="mt-4 flex gap-2">
            <input
              className="min-w-0 flex-1 rounded-xl border p-3"
              placeholder="City"
              value={city}
              onChange={(event) => setCity(event.target.value)}
            />
            <input
              className="w-24 rounded-xl border p-3"
              aria-label="Province"
              value={region}
              onChange={(event) => setRegion(event.target.value)}
            />
            <Button disabled={!city || !region} onClick={() => void addCity()}>
              Add
            </Button>
          </div>
          <div className="mt-4 divide-y">
            {cities
              .filter((item) => item.active)
              .map((item) => (
                <div className="flex items-center justify-between py-3" key={item.id}>
                  <span className="font-bold">
                    {item.city}, {item.region}
                  </span>
                  <button
                    className="text-sm font-bold text-red-700"
                    onClick={() =>
                      void apiFetch(`/v1/contractor/service-cities/${item.id}`, {
                        method: "DELETE",
                      }).then(load)
                    }
                  >
                    Remove
                  </button>
                </div>
              ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
